import React from "react";
import "./PlanUpgradeNeededBox.scss";

import AnimatedDiv from "src/app/components/AnimatedDiv/AnimatedDiv";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";

import { ReactComponent as PadlockIcon } from "src/images/padlock.svg";
import { ReactComponent as CloseIcon } from "src/images/close.svg";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/reducers";
import classNames from "classnames";
import ExternalLink from "src/app/components/ExternalLink/ExternalLink";
import {
  appendMemberUuidToUrl,
  isInternalServiceUrl,
} from "src/utils/internalServices";
import { openAIChatWithHelpCenterUrl } from "src/redux/ai-chat/aiChatActions";
import { SubscriptionEntitlements } from "../../subscriptionUtils";
import PleaseContactAdminPrompt from "../PleaseContactAdminPrompt/PleaseContactAdminPrompt";
import SubscriptionManagementButton, {
  UnlockAddonFeatures,
} from "../SubscriptionManagementButton/SubscriptionManagementButton";
import { useCheckFeatureAccess } from "../../subscriptionFunctions";
import getSubscriptionEntitlementSettings from "./getSubscriptionEntitlementSettings";

interface Props {
  variant?: "info" | "default";
  feature: SubscriptionEntitlements | null;
  onClose?: () => void;
}

function PlanUpgradeNeededBox(props: Props) {
  const { variant = "default", feature, onClose } = props;

  const dispatch = useDispatch();
  const { identity } = useSelector((state: RootState) => state.mainReducer);
  const subscriptionData = identity?.subscriptionData;
  const memberUuid = identity?.id ?? null;
  const isWorkspaceHostnameCustomized =
    identity?.isWorkspaceHostnameCustomized ?? false;

  const hasFeatureAccess = useCheckFeatureAccess(feature);

  const handleLearnMoreClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    url: string,
  ) => {
    if (!isInternalServiceUrl(url)) return;

    event.preventDefault();
    dispatch(openAIChatWithHelpCenterUrl(url));
    onClose?.();
  };

  if (!feature) return null;

  // const seatsQuantity =
  //   subscriptionPlan?.features["feature-seats"]?.unitQuantity;

  const data = getSubscriptionEntitlementSettings(feature);

  const renderBarText = () => {
    if (
      UnlockAddonFeatures.includes(feature) &&
      // seatsQuantity &&
      !hasFeatureAccess
    ) {
      return (
        <IDHFormattedMessage
          id="ws_unlock_after_purchasing_the_add_on"
          defaultMessage="Unlock after purchasing the add-on."
        />
      );
    }

    return (
      <IDHFormattedMessage
        id="ws_unlock_by_upgrading_to_a_higher_plan"
        defaultMessage="Unlock by upgrading to a higher plan."
      />
    );
  };

  return (
    <AnimatedDiv
      className={classNames(
        "plan-upgrade-needed-box",
        `plan-upgrade-needed-box--${variant}`,
        {
          "plan-upgrade-needed-box--actions-hidden":
            isWorkspaceHostnameCustomized,
        },
      )}
    >
      {Boolean(onClose) && (
        <span
          className="plan-upgrade-needed-box__close-button"
          onClick={onClose}
        >
          <CloseIcon />
        </span>
      )}

      <div className="plan-upgrade-needed-box__image">
        <img src={data.image} alt={`${feature}-image`} />

        {subscriptionData && !hasFeatureAccess && (
          <div className="plan-upgrade-needed-box__yellow-bar">
            <PadlockIcon />
            {renderBarText()}
          </div>
        )}
      </div>

      <div className="plan-upgrade-needed-box__heading">
        {variant !== "info" && data.icon && (
          <img
            className="plan-upgrade-needed-box__heading-icon"
            src={data.icon}
            alt="section-icon"
          />
        )}
        <span className="plan-upgrade-needed-box__heading-name">
          <IDHFormattedMessage
            id={data.translation?.id}
            defaultMessage={data.translation?.defaultMessage}
          />
          {variant !== "info" && (
            <>
              &nbsp;
              <IDHFormattedMessage
                id="ws_not_available"
                defaultMessage="not available"
              />
            </>
          )}
        </span>
      </div>
      <p className="plan-upgrade-needed-box__text">
        <IDHFormattedMessage
          id={data.text?.id}
          defaultMessage={data.text?.defaultMessage}
        />
      </p>

      {variant !== "info" && <PleaseContactAdminPrompt />}

      {!isWorkspaceHostnameCustomized && (
        <div className="plan-upgrade-needed-box__buttons">
          {data?.learnMoreUrl && (
            <ExternalLink
              className={classNames(
                "ws-button",
                "ws-button--white-with-light-grey-border",
                "ws-button--large",
              )}
              href={appendMemberUuidToUrl(
                data.learnMoreUrl,
                memberUuid,
                window.location.origin,
              )}
              onClick={(event) =>
                handleLearnMoreClick(event, data.learnMoreUrl)
              }
              data-qa="learn-more-button"
            >
              <IDHFormattedMessage
                id="ws_learn_more"
                defaultMessage="Learn more"
              />
            </ExternalLink>
          )}

          <SubscriptionManagementButton
            feature={feature}
            hasFeatureAccess={hasFeatureAccess}
          />
        </div>
      )}
    </AnimatedDiv>
  );
}

export default PlanUpgradeNeededBox;
